import { zonedTimeToUtc } from 'date-fns-tz'
import { uniq } from 'lodash'
import { v4 } from 'uuid'
import { post } from '~/utils/api'
import {
  Chat,
  ChatCustomBlockLinks,
  ChatCustomBlockUserData,
  ChatCustomUserData,
  ChatHistory,
  ChatHistoryRaw,
  ChatMessageHistoryRaw,
  ChatRaw,
  Client,
  Message,
  MessageLivechatEnd,
  MessageLivechatStart,
  MessageRaw,
  MessageRawCustom,
} from './types'

type ApiResp = {
  data: Client
  chats: ChatHistoryRaw[]
}[]

const getClients = (chats: ChatRaw[]) => {
  const ids = chats.map(chat => chat.chatId)

  return post<ApiResp>('chat/clients', { chats: ids })
}

const decodeHtml = (html: string) => {
  var txt = document.createElement('textarea')

  txt.innerHTML = html

  return txt.value
}

const livechatMsg = (unread: boolean, sentAt?: Date) => ({
  unread,
  sentAt: sentAt || new Date(),
  uid: v4(),
  content: '',
  queueId: 0,
})

export const livechatStartMsg = (
  unread: boolean,
  sentAt?: Date
): MessageLivechatStart => ({
  ...livechatMsg(unread, sentAt),
  type: 'LIVECHAT_START',
})

export const livechatEndMsg = (
  unread: boolean,
  sentAt?: Date
): MessageLivechatEnd => ({
  ...livechatMsg(unread, sentAt),
  type: 'LIVECHAT_END',
})

export const convertChats = async (chats: ChatRaw[], chatAccountId: number) => {
  const clients = await getClients(chats)

  return chats.map((chat, key) => {
    const startedAt = zonedTimeToUtc(
      new Date(chat.chatStart),
      window.c.timezone
    )

    const messages = chat.messages
      .map(message => convertMessage(message))
      .filter(message => message?.content != 'LIVECHAT_AFTER')
      .filter(message => message?.content != 'LIVECHAT_FAIL')
      .filter(message => message) as Message[]

    // nie mamy custom blocków z API przy odświeżaniu strony
    chat.chatCustomBlocks = getCustomBlocksFromMessages(chat)
    // console.log('z wiadomości', getCustomBlocksFromMessages(chat))

    const selectedTags = chat.chatInfo.taggedMulti.map(tag => tag.tag)

    const chatAccount = window.c.chatAccounts.find(
      v => v.id == chatAccountId
    ) as ChatAccount

    const convertedChat: Chat = {
      id: chat.chatId,
      chatAccountId,
      organisationId: chatAccount.orgId,
      active: false,
      stopped: false,
      client: clients[key].data,
      messages,
      system: chat.chatInfo,
      lastUserActivity: chat.chatLastOperatorActivity,
      lastClientActivity: chat.chatLastUserActivity,
      startedAt,
      new: true,
      unread: messages
        .filter(message => message.unread)
        .filter(message => message.type == 'CLIENT').length,
      unreadAt: getUnreadAt(messages),
      links: getLinks(chat),
      customUserData: getChatUserData(chat),
      clientTyping: false,
      tags: chat.chatInfo.tags
        ? chat.chatInfo.tags.map(tag => ({
            ...tag,
            active: selectedTags.includes(tag.id),
          }))
        : [],
      history: [],
      // history: convertHistory(clients[key].chats, chat),
      sessionId: chat.sessionId,
    }

    return convertedChat
  })
}

const getCustomBlocksFromMessages = (chat: ChatRaw) => {
  /**
   *
   * nie lubimy tego, bo co wejście w LC, od początku dostajemy ostatni custom block
   *
   *
   */

  const messages = chat.messages.filter(
    m => m.type == 'CUSTOM'
  ) as MessageRawCustom[]

  // return messages.map(m => JSON.parse(m.message) as object)

  return uniq(messages.map(m => m.message)).map(m => JSON.parse(m) as object)
}

export const convertMessage = (msg: MessageRaw): Message | undefined => {
  const sentAt = zonedTimeToUtc(msg.timestamp, window.c.timezone)

  // nie wiem, czemu to tak było
  // const content = decodeHtml(msg.message)
  const content = msg.message

  const converted = {
    uid: v4(),
    queueId: msg.queueId,
    type: msg.type,
    sentAt,
    content,
    form: undefined,
    unread: msg.unread,
  }

  switch (msg.type) {
    case 'SYSTEM':
      return {
        ...converted,
        content: msg.sender + ' ' + content,
        type: 'INFO',
      }

    case 'REDIRECT_OPERATOR':
      const userFrom = window.getUserByXmpp(msg.src_operator_id.xmpp)?.id
      const userTo = window.getUserByXmpp(msg.operator_id.xmpp)?.id

      if (!userFrom || !userTo) {
        if (!userFrom) {
          console.warn(`operator ${msg.src_operator_id.xmpp} not found`)
        }

        if (!userTo) {
          console.warn(`operator ${msg.operator_id.xmpp} not found`)
        }

        return
      }

      return {
        ...converted,
        userFrom,
        userTo,
        type: 'REDIRECT',
      }

    case 'OPERATOR':
      const userId = window.getUserByXmpp(msg.operator_id.xmpp)?.id

      if (!userId) {
        console.warn(`operator ${msg.operator_id.xmpp} not found`)
        return
      }

      return {
        ...converted,
        type: 'USER',
        userId,
      }

    case 'USER':
      return {
        ...converted,
        type: 'CLIENT',
      }

    case 'PRECHAT':
      if (msg.message == 'connect') {
        return
      }

      if (
        typeof msg.message == 'string' &&
        msg.message.startsWith(`{"template":`)
      ) {
        return
      }

      const type =
        msg.sender == 'User' || msg.sender == 'Użytkownik' ? 'CLIENT' : 'AI'

      if (msg.message !== null && typeof msg.message === 'object') {
        return {
          ...converted,
          type: type,
          form: msg.message,
          content: '',
        }
      }

      return {
        ...converted,
        type: type,
      }
  }
}

export const getUnreadAt = (messages: Message[]) => {
  const unreadMessages = messages.filter(
    message => message.unread && message.queueId > 1
  )

  if (!unreadMessages.length) {
    return
  }

  return unreadMessages.length ? unreadMessages[0].uid : undefined
}

export const getLinks = (chat: ChatRaw): CustomLinkElement[] => {
  if (!chat.chatCustomBlocks) {
    return []
  }

  const linksBlocks = chat.chatCustomBlocks.filter(
    item => item['links']
  ) as ChatCustomBlockLinks[]

  if (!linksBlocks.length) {
    return []
  }

  return linksBlocks[linksBlocks.length - 1].links
}

export const getChatUserData = (chat: ChatRaw): ChatCustomUserData => {
  if (!chat.chatCustomBlocks) {
    return {}
  }

  const userBlocks = chat.chatCustomBlocks.filter(
    item => item['user']
  ) as ChatCustomBlockUserData[]

  if (!userBlocks.length) {
    return {}
  }

  return userBlocks[userBlocks.length - 1].user
}

const convertHistory = (
  chats: ChatHistoryRaw[],
  current: ChatRaw
): ChatHistory[] =>
  chats
    .filter(chat => chat.livechatId != current.chatId)
    .map(chat => ({
      ...chat,
      parts: chat.parts.map(part => {
        const messages = part.messages
          .map(msg => convertHistoryMessage(msg))
          .filter(msg => msg) as Message[]

        return {
          ...part,
          messages: [
            livechatStartMsg(false, messages[0].sentAt),
            ...messages,
            livechatStartMsg(false, messages[messages.length - 1].sentAt),
          ],
        }
      }),
    }))

const convertHistoryMessage = (
  msg: ChatMessageHistoryRaw
): Message | undefined => {
  const converted = {
    uid: v4(),
    queueId: 0,
    type: msg.livechatType,
    sentAt: new Date(msg.createdAt),
    content: msg.content || '',
    form: Array.isArray(msg.script) ? msg.script : undefined,
    unread: false,
  }

  switch (msg.livechatType) {
    case 'SYSTEM':
      return {
        ...converted,
        type: 'INFO',
      }

    case 'REDIRECT_OPERATOR':
      const userFrom = msg.poolUser.id
      const userTo = msg.redirectToUser!.id

      return {
        ...converted,
        userFrom,
        userTo,
        type: 'REDIRECT',
      }

    case 'OPERATOR':
      const userId = msg.poolUser.id

      return {
        ...converted,
        type: 'USER',
        userId,
      }

    case 'USER':
      return {
        ...converted,
        type: 'CLIENT',
      }

    case 'PRECHAT':
      // nie widziałem takich w bazie agent.inteliwise
      // if (msg.content == 'connect') {
      //   return
      // }

      if (msg.content && msg.content.startsWith(`{"template":`)) {
        return
      }

      const type = msg.client ? 'CLIENT' : 'AI'

      return {
        ...converted,
        type: type,
      }
  }
}
